
import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { getProducts, getGroups, migrateLocalStorageToSupabase } from '@/lib/storage';
import { isIosChromeOrSafari } from '@/utils/mobileInitializer';

export const useMigration = (refreshData: () => Promise<boolean>) => {
  const [isMigrating, setIsMigrating] = useState(false);
  const [showMigration, setShowMigration] = useState(false);
  const [migrationAttempted, setMigrationAttempted] = useState(false);
  const [migrationError, setMigrationError] = useState<string | null>(null);
  const { toast } = useToast();

  const checkForLocalData = useCallback((userId: string | undefined) => {
    if (!userId || migrationAttempted) return false;
    
    const localProducts = getProducts();
    const localGroups = getGroups();
    
    if (localProducts.length > 0 || localGroups.length > 0) {
      console.log(`Found local data: ${localProducts.length} products, ${localGroups.length} groups. Will offer migration.`);
      setShowMigration(true);
      return true;
    } else {
      console.log('No local data found that needs migration');
      setShowMigration(false);
      return false;
    }
  }, [migrationAttempted]);

  const handleMigrateData = useCallback(async () => {
    setIsMigrating(true);
    setMigrationAttempted(true);
    setMigrationError(null);
    
    // Detect if we're on iOS Chrome which needs special handling
    const isIOSBrowser = isIosChromeOrSafari();
    const maxRetries = isIOSBrowser ? 2 : 1;
    let retryCount = 0;
    let migrationSuccess = false;
    
    while (retryCount < maxRetries && !migrationSuccess) {
      try {
        // First, make sure we have the latest local data
        const localProducts = getProducts();
        console.log(`Starting migration attempt ${retryCount + 1} with ${localProducts.length} local products`);
        
        if (isIOSBrowser) {
          console.log('Using iOS Chrome specific migration settings');
          // Show a special message for iOS Chrome users
          toast({
            title: "Syncing data",
            description: "This may take longer on iOS. Please don't close the tab.",
            duration: 6000,
          });
        }
        
        const result = await migrateLocalStorageToSupabase();
        
        if (result.success) {
          toast({
            title: "Data migration complete",
            description: isIOSBrowser 
              ? "Your data has been synced to the cloud. Some items might need to be synced again later."
              : "Your data has been successfully synced to the cloud.",
          });
          
          // Force a complete refresh of data from Supabase after migration
          await refreshData();
          
          // Only hide migration prompt after successful migration
          setShowMigration(false);
          
          if (result.results?.products?.count > 0) {
            console.log(`Successfully migrated ${result.results.products.count} products to cloud storage`);
          }
          
          migrationSuccess = true;
        } else {
          console.error("Migration failed:", result.message);
          
          // Handle iOS Chrome specific error
          if (isIOSBrowser && retryCount < maxRetries - 1) {
            console.log(`iOS Chrome sync attempt ${retryCount + 1} failed, will retry...`);
            retryCount++;
            await new Promise(resolve => setTimeout(resolve, 1000)); // Wait before retrying
            continue;
          }
          
          setMigrationError(result.message || "Unknown error");
          
          toast({
            variant: "destructive",
            title: "Sync failed",
            description: result.message || "Failed to sync data to the cloud. Your data is still safe locally.",
          });
          
          // Even if migration fails, still refresh data to ensure we're showing the most up-to-date information
          await refreshData();
        }
      } catch (error) {
        console.error("Migration error:", error);
        
        // For iOS Chrome, retry once
        if (isIOSBrowser && retryCount < maxRetries - 1) {
          console.log(`iOS Chrome sync attempt ${retryCount + 1} failed with error, will retry...`);
          retryCount++;
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait before retrying
          continue;
        }
        
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        setMigrationError(errorMessage);
        
        toast({
          variant: "destructive",
          title: "Sync error",
          description: isIOSBrowser
            ? "Mobile browsers may have limited storage capabilities. Try on desktop for best results."
            : "An unexpected error occurred during sync. Your data is still safe locally.",
        });
        
        // Still refresh data to ensure we're showing the most up-to-date information
        await refreshData();
        break;
      }
      
      retryCount++;
    }
    
    setIsMigrating(false);
  }, [toast, refreshData]);

  return {
    isMigrating,
    showMigration,
    migrationAttempted,
    migrationError,
    checkForLocalData,
    handleMigrateData
  };
};
